import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { useApi } from '../../hooks/useApi';
import { isCveScript } from './cveSummary';
import { isSystemInfoScript } from './systemInfoSummary';
import { REBOOT_SCRIPT, isSummaryScript } from './updateSummary';
import { resultsUrl } from './useEndpointSummary';
import { RebootInfo, UpdateDetail } from './UpdateDetail';
import SoftwareTab from './SoftwareTab';
import SoftwarePolicyTab from './SoftwarePolicyTab';
import { cveOf } from './softwarePolicy';
import { useSoftwareDrift } from './useSoftwareDrift';
import SystemInfoTab from './SystemInfoTab';

// One machine: its Windows updates, its software, how that software compares with the account's software policy, and
// its system info (hardware, age and replacement), each from the latest result of the script that reports it.
// The address says which machine (and which account and device type its schedule belongs to) and which tab.
const TABS = ['updates', 'software', 'policy', 'systeminfo'];

const deviceResultUrl = (account, deviceType, script, deviceId) => {
  const q = new URLSearchParams({ account, deviceType, script, deviceId });
  return `cma/endpointresult/device?${q.toString()}`;
};

function EndpointDevicePage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const theme = useTheme();
  const { deviceId } = useParams();
  const [params, setParams] = useSearchParams();

  const account = params.get('account') || '';
  const deviceType = params.get('type') || 'workstation';
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : TABS[0];

  // Each part is { state: 'loading' | 'ok' | 'unscheduled' | 'none' | 'error', item?, error? }.
  const [updates, setUpdates] = useState({ state: 'loading' });
  const [software, setSoftware] = useState({ state: 'loading' });
  const [systemInfo, setSystemInfo] = useState({ state: 'loading' });
  const [reboot, setReboot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setUpdates({ state: 'loading' });
    setSoftware({ state: 'loading' });
    setSystemInfo({ state: 'loading' });
    setReboot(null);

    // One script's whole result on this machine.
    const readDevice = async (name) => {
      if (!name) return { state: 'unscheduled' };
      try {
        const response = await apiRef.current.request('GET', deviceResultUrl(account, deviceType, name, deviceId));
        return response.data?.item ? { state: 'ok', item: response.data.item } : { state: 'none' };
      } catch (ex) {
        if (ex?.response?.status === 404) return { state: 'none' };
        console.error(`Failed to load the ${name} result:`, ex);
        return { state: 'error', error: ex?.toString?.() || 'Failed to load the result' };
      }
    };

    const load = async () => {
      if (!account) {
        setUpdates({ state: 'error', error: 'The address has no account. Open the machine from the Endpoint Devices page.' });
        setSoftware({ state: 'error', error: 'The address has no account. Open the machine from the Endpoint Devices page.' });
        setSystemInfo({ state: 'error', error: 'The address has no account. Open the machine from the Endpoint Devices page.' });
        return;
      }
      try {
        const response = await apiRef.current.request('GET', resultsUrl(account, deviceType));
        const schedules = Array.isArray(response.data?.schedules) ? response.data.schedules : [];
        const scripts = [...new Set(schedules.flatMap(s => s.scripts || []))];

        const updatesRead = readDevice(scripts.find(isSummaryScript));
        const softwareRead = readDevice(scripts.find(isCveScript));
        const systemInfoRead = readDevice(scripts.find(isSystemInfoScript));
        const rebootName = scripts.find(n => n.toLowerCase() === REBOOT_SCRIPT);

        updatesRead.then(async (u) => {
          if (cancelled) return;
          setUpdates(u);
          // A machine that needs a restart also shows when that restart will happen.
          if (u.state === 'ok' && u.item?.results?.restartRequired) {
            if (!rebootName) {
              setReboot({ state: 'unscheduled' });
              return;
            }
            setReboot({ state: 'loading' });
            const r = await readDevice(rebootName);
            if (!cancelled) setReboot(r.state === 'ok' ? { state: 'ok', item: r.item } : r);
          }
        });
        softwareRead.then((s) => { if (!cancelled) setSoftware(s); });
        systemInfoRead.then((s) => { if (!cancelled) setSystemInfo(s); });
      } catch (ex) {
        if (cancelled) return;
        console.error('Failed to load the machine:', ex);
        const failed = { state: 'error', error: ex?.toString?.() || 'Failed to load the machine' };
        setUpdates(failed);
        setSoftware(failed);
        setSystemInfo(failed);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [account, deviceType, deviceId]);

  // The installed-programs result goes to the API once: it answers with the machine's CVEs (Software tab) and how it
  // compares with the account's software policy (Software policy tab).
  const drift = useSoftwareDrift(account, deviceId, software.state === 'ok' ? software.item?.results : undefined);

  const name = updates.item?.results?.computer || software.item?.results?.computerName
    || systemInfo.item?.results?.ComputerName || deviceId;
  const osName = updates.item?.results?.os?.name || software.item?.results?.os?.name
    || systemInfo.item?.results?.OperatingSystem || '';

  const setTab = (e, value) => {
    const next = new URLSearchParams(params);
    next.set('tab', value);
    setParams(next, { replace: true });
  };

  const body = (part, render) => {
    if (part.state === 'loading') return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;
    if (part.state === 'error') return <Alert severity="error">{part.error}</Alert>;
    if (part.state === 'unscheduled') {
      return <Typography variant="body2" color="text.secondary">This script is not scheduled for this account and device type.</Typography>;
    }
    if (part.state === 'none' || !part.item?.results) {
      return <Typography variant="body2" color="text.secondary">This machine has not reported yet.</Typography>;
    }
    return render(part.item.results);
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        <Box sx={{ pt: 3, pb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              background: alpha(theme.palette.background.paper, 0.6),
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
            </Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Link to="/endpointdevices" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                Endpoint Devices
              </Typography>
            </Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{name}</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                {account && <Chip size="small" variant="outlined" label={account} />}
                <Chip size="small" variant="outlined" label={deviceType} />
                {osName && <Chip size="small" variant="outlined" label={osName} />}
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>{deviceId}</Typography>
              </Stack>
            </Box>
            <Button component={Link} to="/endpointdevices" startIcon={<ArrowBackIcon />}>
              All machines
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 2 }}>
          <Tabs value={tab} onChange={setTab} aria-label="Machine details" sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            <Tab value="updates" label="Updates" />
            <Tab value="software" label="Software" />
            <Tab value="policy" label="Software policy" />
            <Tab value="systeminfo" label="System info" />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {tab === 'updates' && body(updates, (results) => (
              <Stack spacing={2}>
                <RebootInfo reboot={reboot} />
                <UpdateDetail data={results} />
              </Stack>
            ))}
            {tab === 'software' && body(software, (results) => <SoftwareTab data={{ ...results, cve: cveOf(drift) }} />)}
            {tab === 'policy' && body(software, (results) => (
              <SoftwarePolicyTab data={results} drift={drift} account={account} />
            ))}
            {tab === 'systeminfo' && body(systemInfo, (results) => <SystemInfoTab data={results} />)}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default EndpointDevicePage;
